<template>
  <div class="auth-page">
    <div class="auth-card">
      <!-- Brand panel -->
      <aside class="auth-brand">
        <div class="auth-brand-logo">
          <img src="../assets/img/logo.png" alt="Logo" />
        </div>
        <div class="auth-brand-text">
          <h1>{{ appTitle }}</h1>
          <p>Inventory, purchases and issues in one place.</p>
        </div>
        <p class="auth-brand-foot">Secure sign-in</p>
      </aside>

      <!-- Form panel -->
      <main class="auth-form">
        <!-- Sign in -->
        <form v-if="!mustChangePassword" @submit.prevent="submitLogin" novalidate>
          <h2>Welcome back</h2>
          <p class="auth-sub">Sign in with your work email.</p>

          <label for="login-email">Email</label>
          <input id="login-email" ref="email" v-model.trim="email" type="email" class="auth-input"
            autocomplete="username" placeholder="name@company.com" :disabled="busy" required />

          <label for="login-password">Password</label>
          <div class="auth-pass">
            <input id="login-password" ref="password" v-model="password" :type="showPassword ? 'text' : 'password'"
              class="auth-input" autocomplete="current-password" :disabled="busy" required
              @keyup="checkCapsLock" @keydown="checkCapsLock" />
            <button type="button" class="auth-eye" :aria-label="showPassword ? 'Hide password' : 'Show password'"
              @click="showPassword = !showPassword">
              <vue-feather :type="showPassword ? 'eye-off' : 'eye'" size="18"></vue-feather>
            </button>
          </div>
          <p v-if="capsLock" class="auth-hint auth-warn">Caps Lock is on</p>

          <div v-if="error" class="auth-error" role="alert">{{ error }}</div>

          <button class="auth-btn" type="submit" :disabled="busy">
            <span v-if="busy" class="auth-spinner" aria-hidden="true"></span>
            {{ busy ? 'Signing in…' : 'Sign in' }}
          </button>
          <p class="auth-hint auth-center">Forgot your password? Ask your administrator to reset it.</p>
        </form>

        <!-- First login / after admin reset -->
        <form v-else @submit.prevent="submitNewPassword" novalidate>
          <h2>Set a new password</h2>
          <p class="auth-sub">For <strong>{{ email }}</strong>. Choose a password only you know.</p>

          <label for="new-password">New password</label>
          <div class="auth-pass">
            <input id="new-password" ref="newPassword" v-model="newPassword" :type="showPassword ? 'text' : 'password'"
              class="auth-input" autocomplete="new-password" :disabled="busy" required
              @keyup="checkCapsLock" @keydown="checkCapsLock" />
            <button type="button" class="auth-eye" :aria-label="showPassword ? 'Hide password' : 'Show password'"
              @click="showPassword = !showPassword">
              <vue-feather :type="showPassword ? 'eye-off' : 'eye'" size="18"></vue-feather>
            </button>
          </div>

          <label for="confirm-password">Confirm new password</label>
          <input id="confirm-password" v-model="confirmPassword" :type="showPassword ? 'text' : 'password'"
            class="auth-input" autocomplete="new-password" :disabled="busy" required />
          <p v-if="capsLock" class="auth-hint auth-warn">Caps Lock is on</p>

          <ul class="auth-rules">
            <li :class="{ ok: longEnough }">At least {{ minLength }} characters</li>
            <li :class="{ ok: matches }">Passwords match</li>
          </ul>

          <div v-if="error" class="auth-error" role="alert">{{ error }}</div>

          <button class="auth-btn" type="submit" :disabled="busy || !longEnough || !matches">
            <span v-if="busy" class="auth-spinner" aria-hidden="true"></span>
            {{ busy ? 'Saving…' : 'Save and sign in' }}
          </button>
          <button type="button" class="auth-link" @click="backToLogin">Back to sign in</button>
        </form>
      </main>
    </div>
  </div>
</template>

<script>
import { signIn, changePassword, PASSWORD_CHANGE_REQUIRED } from './index';

const MIN_PASSWORD_LENGTH = 8;
const LAST_EMAIL_KEY = 'cims_last_email';

const readLastEmail = () => {
  try { return localStorage.getItem(LAST_EMAIL_KEY) || ''; } catch (e) { return ''; }
};

export default {
  name: 'LoginPage',
  data() {
    return {
      appTitle: process.env.VUE_APP_TITLE || 'CIMS',
      email: readLastEmail(),
      password: '',
      newPassword: '',
      confirmPassword: '',
      showPassword: false,
      capsLock: false,
      mustChangePassword: false,
      busy: false,
      error: '',
      minLength: MIN_PASSWORD_LENGTH,
    };
  },
  computed: {
    longEnough() {
      return this.newPassword.length >= MIN_PASSWORD_LENGTH;
    },
    matches() {
      return this.newPassword.length > 0 && this.newPassword === this.confirmPassword;
    },
  },
  methods: {
    checkCapsLock(event) {
      if (event.getModifierState) this.capsLock = event.getModifierState('CapsLock');
    },
    async submitLogin() {
      this.error = '';
      if (!this.email || !this.password) {
        this.error = 'Enter your email and password.';
        return;
      }
      this.busy = true;
      try {
        await signIn(this.email, this.password);
        try { localStorage.setItem(LAST_EMAIL_KEY, this.email); } catch (e) { /* ignore */ }
      } catch (e) {
        if (e.message === PASSWORD_CHANGE_REQUIRED) {
          this.mustChangePassword = true;
          this.showPassword = false;
          this.$nextTick(() => this.$refs.newPassword && this.$refs.newPassword.focus());
        } else {
          this.error = e.message;
          this.password = '';
          this.$nextTick(() => this.$refs.password && this.$refs.password.focus());
        }
      } finally {
        this.busy = false;
      }
    },
    async submitNewPassword() {
      this.error = '';
      if (!this.longEnough || !this.matches) return;
      this.busy = true;
      try {
        await changePassword(this.email, this.password, this.newPassword);
        try { localStorage.setItem(LAST_EMAIL_KEY, this.email); } catch (e) { /* ignore */ }
      } catch (e) {
        this.error = e.message;
      } finally {
        this.busy = false;
      }
    },
    backToLogin() {
      this.mustChangePassword = false;
      this.password = '';
      this.newPassword = '';
      this.confirmPassword = '';
      this.error = '';
      this.$nextTick(() => this.$refs.password && this.$refs.password.focus());
    },
  },
  mounted() {
    // focus the first empty field
    const target = this.email ? this.$refs.password : this.$refs.email;
    if (target) target.focus();
  },
};
</script>

<style scoped>
.auth-page {
  --auth-accent: #FF9F43;
  --auth-accent-dark: #e8862a;
  --auth-text: #212B36;
  --auth-muted: #637381;
  --auth-border: rgba(145, 158, 171, 0.35);
  --auth-bg: #f4f6f8;
  --auth-card: #ffffff;
  --auth-error-bg: #fdecea;
  --auth-error-text: #b42318;
  --auth-ok: #12805c;

  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 16px;
  background: var(--auth-bg);
  font-family: inherit;
}

.auth-card {
  width: 100%;
  max-width: 920px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  background: var(--auth-card);
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 12px 40px rgba(33, 43, 54, 0.12);
}

/* Brand panel */
.auth-brand {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 32px;
  padding: 40px;
  color: #fff;
  background:
    radial-gradient(circle at 85% 15%, rgba(255, 255, 255, 0.18) 0, rgba(255, 255, 255, 0) 45%),
    radial-gradient(circle at 10% 90%, rgba(0, 0, 0, 0.10) 0, rgba(0, 0, 0, 0) 50%),
    linear-gradient(140deg, #FFB067 0%, var(--auth-accent) 45%, var(--auth-accent-dark) 100%);
}
.auth-brand-logo {
  background: #fff;
  border-radius: 12px;
  padding: 10px 14px;
  width: fit-content;
  max-width: 190px;
}
.auth-brand-logo img {
  display: block;
  width: 100%;
  height: auto;
}
.auth-brand-text h1 {
  margin: 0 0 10px;
  font-size: 30px;
  font-weight: 700;
  line-height: 1.2;
  color: #fff;
}
.auth-brand-text p {
  margin: 0;
  font-size: 15px;
  line-height: 1.5;
  opacity: 0.92;
}
.auth-brand-foot {
  margin: 0;
  font-size: 13px;
  opacity: 0.85;
}

/* Form panel */
.auth-form {
  padding: 48px 44px;
}
.auth-form h2 {
  margin: 0 0 6px;
  font-size: 24px;
  font-weight: 700;
  color: var(--auth-text);
}
.auth-sub {
  margin: 0 0 28px;
  color: var(--auth-muted);
  font-size: 14px;
}
.auth-form label {
  display: block;
  margin: 0 0 8px;
  font-size: 14px;
  font-weight: 600;
  color: var(--auth-text);
}
.auth-input {
  width: 100%;
  height: 44px;
  margin-bottom: 18px;
  padding: 0 14px;
  font-size: 15px;
  color: var(--auth-text);
  background: #fff;
  border: 1px solid var(--auth-border);
  border-radius: 8px;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.auth-input:focus {
  outline: none;
  border-color: var(--auth-accent);
  box-shadow: 0 0 0 3px rgba(255, 159, 67, 0.22);
}
.auth-input:disabled {
  background: #f9fafb;
}
.auth-pass {
  position: relative;
}
.auth-pass .auth-input {
  padding-right: 46px;
}
.auth-eye {
  position: absolute;
  top: 0;
  right: 0;
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 0;
  background: transparent;
  color: var(--auth-muted);
  cursor: pointer;
  border-radius: 0 8px 8px 0;
}
.auth-eye:hover,
.auth-eye:focus-visible {
  color: var(--auth-text);
  outline: none;
}

.auth-hint {
  margin: -10px 0 16px;
  font-size: 13px;
  color: var(--auth-muted);
}
.auth-warn {
  color: #b54708;
  font-weight: 600;
}
.auth-center {
  margin: 18px 0 0;
  text-align: center;
}

.auth-rules {
  list-style: none;
  margin: -4px 0 18px;
  padding: 0;
  font-size: 13px;
  color: var(--auth-muted);
}
.auth-rules li {
  margin-bottom: 4px;
}
.auth-rules li::before {
  content: '○';
  display: inline-block;
  width: 18px;
}
.auth-rules li.ok {
  color: var(--auth-ok);
}
.auth-rules li.ok::before {
  content: '✓';
}

.auth-error {
  margin: 0 0 16px;
  padding: 10px 12px;
  border-radius: 8px;
  font-size: 14px;
  background: var(--auth-error-bg);
  color: var(--auth-error-text);
}

.auth-btn {
  width: 100%;
  height: 46px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  border: 0;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 700;
  color: #fff;
  background: var(--auth-accent);
  cursor: pointer;
  transition: background 0.15s ease;
}
.auth-btn:hover:not(:disabled) {
  background: var(--auth-accent-dark);
}
.auth-btn:focus-visible {
  outline: 3px solid rgba(255, 159, 67, 0.4);
  outline-offset: 2px;
}
.auth-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.auth-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.5);
  border-top-color: #fff;
  border-radius: 50%;
  animation: auth-spin 0.7s linear infinite;
}
@keyframes auth-spin {
  to { transform: rotate(360deg); }
}

.auth-link {
  display: block;
  margin: 16px auto 0;
  border: 0;
  background: none;
  color: var(--auth-accent-dark);
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
}
.auth-link:hover {
  text-decoration: underline;
}

/* Phones / small tablets: single column, compact brand header */
@media (max-width: 767.98px) {
  .auth-card {
    grid-template-columns: 1fr;
    max-width: 440px;
  }
  .auth-brand {
    padding: 24px;
    gap: 16px;
  }
  .auth-brand-text h1 {
    font-size: 22px;
    margin-bottom: 4px;
  }
  .auth-brand-text p {
    font-size: 14px;
  }
  .auth-brand-foot {
    display: none;
  }
  .auth-form {
    padding: 28px 24px 32px;
  }
}
</style>
